import { REQUEST_LOADING_STATES } from '../../../helpers/constant';
import { createSlice } from '@reduxjs/toolkit';
import { ScriptCategoryDto, ScriptDto } from '../../../helpers/types';
import { createScriptCategoryAction, deleteScriptCategoryAction, fetchScriptCategoriesByCreator, fetchScriptsByFilter, updateScriptCategoryAction } from '../thunks/scriptThunks';

interface CategoriesCache {
  creatorId: string;
  categories: ScriptCategoryDto[];
  ttl?: number;
  llt?: number;
}

interface ScriptState {
  loading: string;
  error: string | null;
  categoriesByCreator: CategoriesCache[];
  scriptsByKey: Array<{
    key: string;
    items: ScriptDto[];
    page: number;
    totalPages: number;
    ttl?: number;
    llt?: number;
  }>;
  loadingByKey: Record<string, string>;
}

const initialState: ScriptState = {
  loading: REQUEST_LOADING_STATES.IDLE,
  error: null,
  categoriesByCreator: [],
  scriptsByKey: [],
  loadingByKey: {},
};

const scriptSlice = createSlice({
  name: 'script',
  initialState,
  reducers: {
    clearScriptsCache: (state) => {
      state.scriptsByKey = [];
      state.loadingByKey = {};
    },
    removeScriptFromCache: (state, action) => {
      const { creatorId, id } = action.payload as { creatorId: string; id: string };
      const keyPrefix = `${creatorId}__`;
      state.scriptsByKey = state.scriptsByKey.map((entry) => {
        if (!entry.key.startsWith(keyPrefix)) return entry;
        const filtered = entry.items.filter((it: ScriptDto) => it._id !== id);
        return { ...entry, items: filtered } as typeof entry;
      });
    },
    reorderScriptsCache: (state, action) => {
      const { creatorId, filterOrCategoryId, start, end } = action.payload as { creatorId: string; filterOrCategoryId: string; start: number; end: number };
      const key = `${creatorId}__${filterOrCategoryId}`;
      const idx = state.scriptsByKey.findIndex((e) => e.key === key);
      if (idx < 0) return;
      const items = state.scriptsByKey[idx].items;
      if (start < 0 || start >= items.length || end < 0 || end >= items.length) return;
      const moved = items.splice(start, 1)[0];
      items.splice(end, 0, moved);
      const stampAll = filterOrCategoryId === 'all';
      items.forEach((it: any, i: number) => {
        if (stampAll) it.globalOrder = i;
        else it.categoryOrder = i;
      });
      state.scriptsByKey[idx].llt = Date.now();
    },
    setScriptsForKey: (state, action) => {
      const { creatorId, filterOrCategoryId, items } = action.payload as { creatorId: string; filterOrCategoryId: string; items: ScriptDto[] };
      const key = `${creatorId}__${filterOrCategoryId}`;
      const idx = state.scriptsByKey.findIndex((e) => e.key === key);
      if (idx >= 0) {
        state.scriptsByKey[idx].items = items as any;
        const stampAll = filterOrCategoryId === 'all';
        (state.scriptsByKey[idx].items as any[]).forEach((it: any, i: number) => {
          if (stampAll) it.globalOrder = i;
          else it.categoryOrder = i;
        });
        state.scriptsByKey[idx].llt = Date.now();
      } else {
        const stampAll = filterOrCategoryId === 'all';
        const stamped = (items as any[]).map((it: any, i: number) => ({
          ...it,
          ...(stampAll ? { globalOrder: i } : { categoryOrder: i }),
        }));
        state.scriptsByKey.push({
          key,
          items: stamped as any,
          page: 1,
          totalPages: state.scriptsByKey.find((e) => e.key === key)?.totalPages || 1,
          ttl: 5,
          llt: Date.now(),
        } as any);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScriptCategoriesByCreator.pending, (state) => {
        state.loading = REQUEST_LOADING_STATES.PENDING;
        state.error = null;
      })
      .addCase(fetchScriptCategoriesByCreator.fulfilled, (state, action) => {
        state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
        const { creatorId, categories } = action.payload;
        const idx = state.categoriesByCreator.findIndex((c) => c.creatorId === creatorId);
        if (idx >= 0) {
          state.categoriesByCreator[idx].categories = categories;
          state.categoriesByCreator[idx].llt = Date.now();
        } else {
          state.categoriesByCreator.push({ creatorId, categories, ttl: 5, llt: Date.now() });
        }
      })
      .addCase(fetchScriptCategoriesByCreator.rejected, (state, action) => {
        state.loading = REQUEST_LOADING_STATES.FAILED;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to fetch categories';
      })
      .addCase(createScriptCategoryAction.fulfilled, (state, action) => {
        const { creatorId, category } = action.payload;
        const idx = state.categoriesByCreator.findIndex((c) => c.creatorId === creatorId);
        if (idx >= 0) {
          state.categoriesByCreator[idx].categories = [...state.categoriesByCreator[idx].categories, category];
          state.categoriesByCreator[idx].llt = Date.now();
        } else {
          state.categoriesByCreator.push({ creatorId, categories: [category], ttl: 5, llt: Date.now() });
        }
      })
      .addCase(updateScriptCategoryAction.fulfilled, (state, action) => {
        const { id, name } = action.payload;
        state.categoriesByCreator = state.categoriesByCreator.map((entry) => ({
          ...entry,
          categories: entry.categories.map((c: ScriptCategoryDto) => (c._id === id ? { ...c, name } : c)),
        }));
      })
      .addCase(deleteScriptCategoryAction.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.categoriesByCreator = state.categoriesByCreator.map((entry) => ({
          ...entry,
          categories: entry.categories.filter((c) => c._id !== id),
        }));
      })
      .addCase(fetchScriptsByFilter.pending, (state, action) => {
        const { creatorId, filterOrCategoryId } = action.meta.arg;
        const key = `${creatorId}__${filterOrCategoryId}`;
        state.loadingByKey[key] = REQUEST_LOADING_STATES.PENDING;
        state.error = null;
      })
      .addCase(fetchScriptsByFilter.fulfilled, (state, action) => {
        const { key, page, items, totalPages } = action.payload;
        state.loadingByKey[key] = REQUEST_LOADING_STATES.SUCCEEDED;
        const idx = state.scriptsByKey.findIndex((e) => e.key === key);
        if (idx >= 0) {
          if (page <= 1) {
            state.scriptsByKey[idx].items = items;
          } else {
            const seen = new Set(state.scriptsByKey[idx].items.map((i: ScriptDto) => i._id));
            const toAdd = (items as ScriptDto[]).filter((i: ScriptDto) => !seen.has(i._id));
            state.scriptsByKey[idx].items.push(...toAdd);
          }
          state.scriptsByKey[idx].page = page;
          state.scriptsByKey[idx].totalPages = totalPages;
          state.scriptsByKey[idx].llt = Date.now();
        } else {
          state.scriptsByKey.push({ key, items, page, totalPages, ttl: 5, llt: Date.now() });
        }
      })
      .addCase(fetchScriptsByFilter.rejected, (state, action) => {
        const { creatorId, filterOrCategoryId } = action.meta.arg as any;
        const key = `${creatorId}__${filterOrCategoryId}`;
        state.loadingByKey[key] = REQUEST_LOADING_STATES.FAILED;
        state.error = (action.payload as any)?.message || action.error.message || 'Failed to fetch scripts';
      });
  },
});

export const { clearScriptsCache, reorderScriptsCache, setScriptsForKey, removeScriptFromCache } = scriptSlice.actions;
export default scriptSlice.reducer;
